import * as React from "react";
import { useState, useEffect } from "react";
import {
  Stack,
  Text,
  SearchBox,
  Spinner,
  SpinnerSize,
  Link,
  TooltipHost,
  Pivot,
  PivotItem,
  mergeStyles,
  getTheme,
} from "@fluentui/react";
import { apiBaseUrl } from "../../../auth/authConfig";

const API_URL = apiBaseUrl();
const theme = getTheme();

type Usage = "metrics" | "info" | "both" | "branches" | "market" | null;

type FieldDefinition = {
  field: string;
  title: string;
  description: string;
  usage: Usage;
};

const termStyle = mergeStyles({
  padding: "6px 0",
  borderBottom: `1px solid ${theme.palette.neutralLighter}`,
  selectors: {
    ":last-child": { borderBottom: "none" },
  },
});

const fieldNameStyle = mergeStyles({
  fontFamily: "Consolas, Menlo, monospace",
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.themePrimary,
  cursor: "pointer",
  border: "none",
  background: "transparent",
  padding: 0,
  selectors: {
    ":hover": { textDecoration: "underline" },
  },
});

const fieldNameCopiedStyle = mergeStyles({
  color: theme.palette.green,
});

// Outline chips stay legible on any theme background.
const badgeBase = {
  fontSize: 9,
  fontWeight: 600 as const,
  letterSpacing: "0.4px",
  padding: "0 5px",
  borderRadius: 8,
  lineHeight: "14px",
};

const metricsBadgeStyle = mergeStyles(badgeBase, {
  color: theme.palette.themePrimary,
  border: `1px solid ${theme.palette.themePrimary}`,
});

const infoBadgeStyle = mergeStyles(badgeBase, {
  color: theme.palette.teal,
  border: `1px solid ${theme.palette.teal}`,
});

const branchesBadgeStyle = mergeStyles(badgeBase, {
  color: theme.palette.orangeLighter,
  border: `1px solid ${theme.palette.orangeLighter}`,
});

const marketBadgeStyle = mergeStyles(badgeBase, {
  color: theme.palette.purple,
  border: `1px solid ${theme.palette.purple}`,
});

// Which function(s) a glossary term works with. usage === null never renders —
// reference-only terms are filtered out below.
const UsageBadges: React.FunctionComponent<{ usage: Usage }> = ({ usage }) => {
  if (usage === null) return null;
  return (
    <>
      {(usage === "metrics" || usage === "both") && <span className={metricsBadgeStyle}>METRICS</span>}
      {(usage === "info" || usage === "both") && <span className={infoBadgeStyle}>INFO</span>}
      {usage === "branches" && <span className={branchesBadgeStyle}>BRANCHES</span>}
      {usage === "market" && <span className={marketBadgeStyle}>MARKET</span>}
    </>
  );
};

const CopyableField: React.FunctionComponent<{ field: string }> = ({ field }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(field).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <TooltipHost content={copied ? "Copied!" : "Click to copy"}>
      <button
        type="button"
        className={copied ? `${fieldNameStyle} ${fieldNameCopiedStyle}` : fieldNameStyle}
        onClick={handleCopy}
      >
        {field}
      </button>
    </TooltipHost>
  );
};

type UsageFilter = "all" | "metrics" | "info" | "branches" | "market";

const HelpGlossary: React.FunctionComponent = () => {
  const [fields, setFields] = useState<FieldDefinition[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [usageFilter, setUsageFilter] = useState<UsageFilter>("all");

  const loadFields = async () => {
    setError(null);
    setFields(null);
    try {
      const response = await fetch(`${API_URL}/fields`);
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      setFields((await response.json()) as FieldDefinition[]);
    } catch (err) {
      setError(`Could not load field glossary: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  useEffect(() => {
    loadFields();
  }, []);

  const needle = filter.trim().toLowerCase();

  const matchesText = (f: FieldDefinition) =>
    needle === "" ||
    f.field.toLowerCase().includes(needle) ||
    (f.title ?? "").toLowerCase().includes(needle) ||
    (f.description ?? "").toLowerCase().includes(needle);

  const matchesUsage = (f: FieldDefinition) =>
    usageFilter === "all" ||
    f.usage === usageFilter ||
    (f.usage === "both" && (usageFilter === "metrics" || usageFilter === "info"));

  // usage === null means reference-only: the term is defined in an FDIC spec
  // but we don't ingest data for it yet, so neither function would return
  // anything. Hidden for now to keep the MVP glossary to fields that work —
  // the API still serves all ~2,400 terms if we want a toggle later.
  const matches = fields?.filter((f) => f.usage !== null && matchesText(f) && matchesUsage(f)) ?? [];
  // The glossary holds ~2,500 FDIC terms — rendering them all makes the pane
  // crawl, so cap the list and point at the filter box instead.
  const RENDER_CAP = 200;
  const visible = matches.slice(0, RENDER_CAP);

  return (
    <Stack tokens={{ childrenGap: 8 }}>
      <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
        Field names you can use in SHEETYBANKS.METRICS, INFO, BRANCHES and MARKET. Click a field name
        to copy it.
      </Text>
      <SearchBox
        placeholder="Filter fields..."
        value={filter}
        onChange={(_e, val) => setFilter(val || "")}
        styles={{ root: { height: 28 } }}
      />

      <Pivot
        headersOnly
        selectedKey={usageFilter}
        onLinkClick={(item) => setUsageFilter((item?.props.itemKey as UsageFilter) ?? "all")}
        styles={{ link: { height: 28, lineHeight: 28 } }}
      >
        <PivotItem headerText="All" itemKey="all" />
        <PivotItem headerText="METRICS" itemKey="metrics" />
        <PivotItem headerText="INFO" itemKey="info" />
        <PivotItem headerText="BRANCHES" itemKey="branches" />
        <PivotItem headerText="MARKET" itemKey="market" />
      </Pivot>

      {!fields && !error && <Spinner size={SpinnerSize.medium} label="Loading glossary..." />}

      {error && (
        <Text variant="small" styles={{ root: { color: theme.palette.redDark } }}>
          {error} <Link onClick={loadFields}>Retry</Link>
        </Text>
      )}

      {fields && visible.length === 0 && (
        <Text variant="small" styles={{ root: { color: theme.palette.neutralTertiary } }}>
          {fields.length === 0 ? "No field definitions loaded on the server yet." : "No fields match your filter."}
        </Text>
      )}

      {matches.length > RENDER_CAP && (
        <Text variant="small" styles={{ root: { color: theme.palette.neutralTertiary } }}>
          Showing first {RENDER_CAP} of {matches.length} fields — type to narrow the list.
        </Text>
      )}

      <div>
        {visible.map((f) => (
          <div key={f.field} className={termStyle}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
              <CopyableField field={f.field} />
              <UsageBadges usage={f.usage} />
              <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                {f.title}
              </Text>
            </Stack>
            {f.description && (
              <Text
                variant="small"
                styles={{ root: { color: theme.palette.neutralSecondary, display: "block" } }}
              >
                {f.description}
              </Text>
            )}
          </div>
        ))}
      </div>
    </Stack>
  );
};

export default HelpGlossary;
